import { CareOSLoader } from '@/components/ui/careos-loader';
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import { supabase } from '@/integrations/supabase/client';
import { useCompany } from '@/hooks/useCompany';
import { useAuth } from '@/contexts/AuthContext';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { SignedImage } from '@/components/ui/signed-image';
import { ImageCropModal } from '@/components/ui/image-crop-modal';
import { extractPathFromUrl } from '@/lib/storage-utils';
import { Building2, Loader2, Upload, Trash2, Settings as SettingsIcon, Crown } from 'lucide-react';
import { toast } from 'sonner';

interface CompanyRow {
  id: string;
  name: string;
  logo_url: string | null;
  owner_id: string;
  created_at: string;
}

export default function CompanySettings() {
  const { t, i18n } = useTranslation();
  const { company, isCompanyManager, isCompanyMember, isLoading: isCompanyLoading } = useCompany();
  const { subscription } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [ownerId, setOwnerId] = useState<string>('');
  const [createdAt, setCreatedAt] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [assignmentsCount, setAssignmentsCount] = useState<number>(0);

  const [cropFile, setCropFile] = useState<File | null>(null);
  const [cropOpen, setCropOpen] = useState(false);

  const locale = i18n.language?.startsWith('pt') ? ptBR : enUS;

  useEffect(() => {
    const loadCompany = async () => {
      if (!company?.companyId) return;
      setLoading(true);
      const [companyRes, assignmentsRes] = await Promise.all([
        supabase
          .from('care_companies')
          .select('id, name, logo_url, owner_id, created_at')
          .eq('id', company.companyId)
          .maybeSingle(),
        supabase
          .from('company_patient_assignments')
          .select('id', { count: 'exact', head: true })
          .eq('company_id', company.companyId)
          .eq('is_active', true),
      ]);

      if (companyRes.error || !companyRes.data) {
        console.error('Error loading company:', companyRes.error);
        toast.error(t('company.saveError'));
        setLoading(false);
        return;
      }

      const c = companyRes.data as CompanyRow;
      setName(c.name);
      setLogoUrl(c.logo_url);
      setOwnerId(c.owner_id);
      setCreatedAt(c.created_at);
      setAssignmentsCount(assignmentsRes.count ?? 0);
      setLoading(false);
    };
    loadCompany();
  }, [company?.companyId, t]);

  // Redirect non-members (after hooks)
  if (!isCompanyLoading && !isCompanyMember) {
    return <Navigate to="/app" replace />;
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(t('common.invalidImage', { defaultValue: 'Arquivo inválido' }));
      return;
    }
    setCropFile(file);
    setCropOpen(true);
    // reset input so same file can be picked again
    e.target.value = '';
  };

  const handleCrop = async (blob: Blob) => {
    if (!company?.companyId) return;
    setCropOpen(false);
    setUploading(true);

    try {
      const filePath = `${company.companyId}/logo-${Date.now()}.jpg`;
      const { error: uploadError } = await supabase.storage
        .from('company-logos')
        .upload(filePath, blob, {
          contentType: 'image/jpeg',
          upsert: true,
        });

      if (uploadError) throw uploadError;

      // Delete previous logo file if any
      if (logoUrl) {
        const prevPath = extractPathFromUrl(logoUrl, 'company-logos');
        if (prevPath && prevPath !== filePath) {
          await supabase.storage.from('company-logos').remove([prevPath]);
        }
      }

      const { error: updateError } = await supabase
        .from('care_companies')
        .update({ logo_url: filePath })
        .eq('id', company.companyId);

      if (updateError) throw updateError;

      setLogoUrl(filePath);
      await queryClient.invalidateQueries({ queryKey: ['company-membership'] });
      toast.success(t('company.saveSuccess'));
    } catch (err) {
      console.error('Error uploading logo:', err);
      toast.error(t('company.saveError'));
    } finally {
      setUploading(false);
      setCropFile(null);
    }
  };

  const handleRemoveLogo = async () => {
    if (!company?.companyId || !logoUrl) return;
    setUploading(true);
    try {
      const prevPath = extractPathFromUrl(logoUrl, 'company-logos');
      if (prevPath) {
        await supabase.storage.from('company-logos').remove([prevPath]);
      }
      const { error } = await supabase
        .from('care_companies')
        .update({ logo_url: null })
        .eq('id', company.companyId);
      if (error) throw error;
      setLogoUrl(null);
      await queryClient.invalidateQueries({ queryKey: ['company-membership'] });
      toast.success(t('company.saveSuccess'));
    } catch (err) {
      console.error('Error removing logo:', err);
      toast.error(t('company.saveError'));
    } finally {
      setUploading(false);
    }
  };

  const handleSaveName = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!company?.companyId) return;
    const trimmed = name.trim();
    if (!trimmed) return;
    if (trimmed.length > 100) {
      toast.error(t('company.nameTooLong'));
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase
        .from('care_companies')
        .update({ name: trimmed })
        .eq('id', company.companyId);
      if (error) throw error;
      await queryClient.invalidateQueries({ queryKey: ['company-membership'] });
      toast.success(t('company.saveSuccess'));
    } catch (err) {
      console.error('Error saving company name:', err);
      toast.error(t('company.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (isCompanyLoading || loading) {
    return (
      <AppLayout title={t('company.settingsTitle')} showBack>
        <div className="flex items-center justify-center p-8">
          <CareOSLoader size="md" />
        </div>
      </AppLayout>
    );
  }

  const readOnly = !isCompanyManager;

  return (
    <AppLayout title={t('company.settingsTitle')} showBack>
      <div className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="p-2 bg-primary/10 rounded-lg shrink-0">
            <SettingsIcon className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="text-base font-semibold">{t('company.settingsTitle')}</h2>
            <p className="text-sm text-muted-foreground">
              {t('company.settingsDescription')}
            </p>
          </div>
        </div>

        {/* Logo card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('company.companyLogo')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="h-20 w-20 rounded-2xl bg-muted flex items-center justify-center overflow-hidden shrink-0">
                {logoUrl ? (
                  <SignedImage
                    src={logoUrl}
                    bucket="company-logos"
                    className="h-full w-full object-cover"
                    alt={name}
                    fallback={<Building2 className="h-8 w-8 text-muted-foreground" />}
                  />
                ) : (
                  <Building2 className="h-8 w-8 text-muted-foreground" />
                )}
              </div>
              {!readOnly && (
                <div className="flex flex-col gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileSelect}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    {uploading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    {t('company.uploadLogo')}
                  </Button>
                  {logoUrl && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleRemoveLogo}
                      disabled={uploading}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      {t('company.removeLogo')}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Name card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('company.companyName')}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSaveName} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="companyName">{t('company.companyName')}</Label>
                <Input
                  id="companyName"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('company.companyNamePlaceholder')}
                  maxLength={100}
                  disabled={readOnly || saving}
                  required
                />
              </div>
              {!readOnly && (
                <Button type="submit" disabled={saving || !name.trim()}>
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('common.saving')}
                    </>
                  ) : (
                    t('common.save')
                  )}
                </Button>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Plan and usage card — manager only */}
        {isCompanyManager && (() => {
          const hasCompanyPlan = subscription.subscribed && subscription.isCompanyPlan;
          const totalSlots = hasCompanyPlan ? (subscription.patientSlots ?? 1) : 1;
          const used = assignmentsCount;
          const remaining = Math.max(0, totalSlots - used);
          const percent = totalSlots > 0 ? Math.min(100, Math.round((used / totalSlots) * 100)) : 0;
          const limitReached = used >= totalSlots;

          return (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('company.planAndUsageTitle')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Plan status */}
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg shrink-0 ${hasCompanyPlan ? 'bg-primary/10' : 'bg-muted'}`}>
                    <Crown className={`h-5 w-5 ${hasCompanyPlan ? 'text-primary' : 'text-muted-foreground'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">
                        {hasCompanyPlan ? t('company.companyPlanName') : t('company.freePlanName')}
                      </span>
                      <Badge variant={hasCompanyPlan ? 'default' : 'secondary'}>
                        {hasCompanyPlan ? t('company.planActive') : t('company.planNone')}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {hasCompanyPlan && subscription.subscriptionEnd
                        ? t('company.renewsOn', {
                            date: format(new Date(subscription.subscriptionEnd), 'PP', { locale }),
                          })
                        : t('company.noPlanDescription')}
                    </p>
                  </div>
                </div>

                {/* Usage */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{t('company.activePatients')}</span>
                    <span className="font-medium">{used} / {totalSlots}</span>
                  </div>
                  <Progress
                    value={percent}
                    indicatorClassName={limitReached ? 'bg-destructive' : undefined}
                  />
                  <p className={`text-xs ${limitReached ? 'text-destructive' : 'text-muted-foreground'}`}>
                    {limitReached
                      ? t('company.limitReached')
                      : t('company.slotsAvailable', { count: remaining })}
                  </p>
                </div>

                {/* Action */}
                <div className="flex justify-end pt-2">
                  <Button
                    type="button"
                    variant={hasCompanyPlan ? 'outline' : 'default'}
                    size="sm"
                    onClick={() => navigate('/app/subscription')}
                  >
                    {hasCompanyPlan ? t('company.manageSubscription') : t('company.viewPlans')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })()}


        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('company.companyInfo')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">{t('company.createdAt')}</span>
              <span className="font-medium">
                {createdAt ? format(new Date(createdAt), 'PP', { locale }) : '—'}
              </span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">{t('company.companyId')}</span>
              <span className="font-mono text-xs truncate max-w-[60%]" title={company?.companyId}>
                {company?.companyId}
              </span>
            </div>
          </CardContent>
        </Card>

        <ImageCropModal
          open={cropOpen}
          imageFile={cropFile}
          onClose={() => {
            setCropOpen(false);
            setCropFile(null);
          }}
          onCrop={handleCrop}
          size={512}
        />
      </div>
    </AppLayout>
  );
}
